import React from 'react';

function TermsOfService() {
  return (
    <div className="max-w-3xl mx-auto px-5 py-10 font-body leading-relaxed text-slate-900">
      <h1 className="font-heading text-3xl font-bold mb-2">Terms of Service</h1>
      <p className="text-slate-500 mb-4">Last Updated: February 5, 2026</p>

      <div className="mb-8 p-4 bg-slate-100 rounded-xl text-sm">
        <p className="font-semibold text-slate-700">Resmaker.ai is owned and operated by Impulse Theory LLC, a California limited liability company.</p>
        <p className="mt-2 text-slate-600">Contact: support@impulsetheory.com</p>
      </div>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
        <p>By accessing or using Resmaker.ai ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Service.</p>
        <p className="mt-3 text-slate-600 text-sm">These Terms constitute a legally binding agreement between you and Impulse Theory LLC. You acknowledge that you are entering into this agreement with Impulse Theory LLC, a limited liability company, and not with any individual owner, member, or employee of the Company.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">2. Description of Service</h2>
        <p>Resmaker.ai is a resume tailoring application that uses artificial intelligence to help users customize their resumes for specific job applications. The Service includes:</p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li>Resume storage and management</li>
          <li>AI-powered resume tailoring based on job descriptions</li>
          <li>Application tracking and organization</li>
          <li>Q&A history for capturing insights about companies</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">3. Eligibility</h2>
        <p>You must be at least 18 years old to use the Service. By using the Service, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">4. User Data and Responsibilities</h2>
        <p><strong>Account Creation:</strong> You must provide accurate information and maintain the confidentiality of your credentials.</p>
        <p className="mt-3"><strong>Data You Provide:</strong> By using the Service, you grant us permission to store and process your resume content using third-party AI services (Claude API by Anthropic).</p>
        <p className="mt-3"><strong>Data Ownership:</strong> You retain all ownership rights to your resume content. You may export or delete your data at any time.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">5. Use Restrictions</h2>
        <p>You agree NOT to:</p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li>Use the Service for any illegal purpose</li>
          <li>Attempt to gain unauthorized access to the Service</li>
          <li>Upload malicious code or harmful content</li>
          <li>Generate discriminatory, offensive, or fraudulent content</li>
          <li>Reverse engineer or extract source code</li>
          <li>Resell or commercialize the Service without permission</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">6. AI-Generated Content</h2>
        <p><strong>No Guarantees:</strong> AI-generated content is provided "as-is" without guarantees of accuracy. You are solely responsible for reviewing all AI-generated content before use.</p>
        <p className="mt-3"><strong>Your Responsibility:</strong> You must ensure all content in your resume is truthful and accurate. You are solely responsible for content you submit to potential employers.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">7. Credits and Billing</h2>
        <p><strong>Credit System:</strong> The Service operates on a credit-based system. Certain AI-powered features consume credits when used, including resume tailoring, job analysis, and interview preparation guides.</p>
        <p className="mt-3"><strong>Free Credits:</strong> New accounts receive a limited number of free credits. Free credits are provided as a trial and have no cash value.</p>
        <p className="mt-3"><strong>Purchased Credits:</strong> Additional credits may be purchased through our payment processor (Stripe). All purchases are final.</p>
        <p className="mt-3"><strong>No Cash Value:</strong> Credits have no cash or monetary value and cannot be exchanged, transferred, or redeemed for cash under any circumstances.</p>
        <p className="mt-3"><strong>Non-Refundable:</strong> All credit purchases are non-refundable. Once credits are purchased, they cannot be returned or refunded, except where required by applicable law.</p>
        <p className="mt-3"><strong>Credit Expiration:</strong> We reserve the right to expire unused credits on inactive accounts (12+ months of inactivity).</p>
        <p className="mt-3"><strong>Usage:</strong> Credits are consumed when AI features are invoked.</p>
        <p className="mt-3"><strong>Price Changes:</strong> We reserve the right to change credit pricing at any time.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
        <p className="uppercase font-semibold text-sm">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY OF AI-GENERATED CONTENT, OR UNINTERRUPTED OPERATION.</p>
        <p className="mt-3 text-sm">YOU USE THE SERVICE AT YOUR OWN RISK.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
        <p className="uppercase font-semibold text-sm">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
        <ul className="pl-6 mt-2 list-disc space-y-1 text-sm">
          <li>Impulse Theory LLC, its members, managers, officers, employees, and agents are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
          <li>We are not responsible for job application outcomes or employment decisions</li>
          <li>We are not liable for data loss (maintain backups)</li>
          <li>Our total liability for any claim is limited to the greater of: (a) the amount you paid us in the 12 months preceding the claim, or (b) $100 USD</li>
        </ul>
        <p className="mt-3 text-sm text-slate-600">You acknowledge that these limitations are an essential element of the agreement between you and Impulse Theory LLC.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">10. Indemnification</h2>
        <p className="text-sm">You agree to indemnify, defend, and hold harmless Impulse Theory LLC, its members, managers, officers, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service, violation of these Terms, or content you submit.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">11. Dispute Resolution</h2>
        <p><strong>Informal Resolution:</strong> Before filing any formal dispute, contact us at support@impulsetheory.com to attempt resolution.</p>
        <p className="mt-3"><strong>Binding Arbitration:</strong> Any unresolved dispute shall be determined by binding arbitration in California, administered by JAMS.</p>
        <p className="mt-3 font-semibold text-sm">CLASS ACTION WAIVER: You agree to bring claims only in your individual capacity and not as a plaintiff or class member in any class action.</p>
        <p className="mt-3 text-sm text-slate-600">You may opt out of arbitration by emailing support@impulsetheory.com within 30 days of accepting these Terms.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">12. Governing Law</h2>
        <p>These Terms shall be governed by the laws of the State of California, without regard to conflict of law provisions.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">13. Changes and Termination</h2>
        <p>We may modify, suspend, or discontinue the Service at any time. We may terminate accounts that violate these Terms. You may terminate your account at any time.</p>
        <p className="mt-3"><strong>Effect on Credits:</strong> Upon account termination (by you or us), any remaining credits are forfeited and cannot be refunded or transferred.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">14. Contact</h2>
        <p><strong>Impulse Theory LLC</strong></p>
        <p className="mt-2">Email: support@impulsetheory.com</p>
        <p className="mt-2 text-sm text-slate-600">For legal notices, use subject line: "Legal Notice"</p>
      </section>

      <div className="mt-12 p-4 bg-slate-100 rounded-xl text-sm text-slate-600">
        <p className="font-semibold text-slate-700">By using Resmaker.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
        <p className="mt-2">You further acknowledge that these Terms constitute a binding legal agreement between you and Impulse Theory LLC, a California limited liability company.</p>
      </div>
    </div>
  );
}

export default TermsOfService;
